import React, { useState, useCallback, memo } from 'react';
import { Folder, ChevronRight, ChevronDown } from 'lucide-react';
import FileTypeIcon from '@/components/FileTypeIcon';

interface FileNode {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: FileNode[];
  size?: number;
  modifiedAt?: string;
  extension?: string;
}

interface ProjectFileListProps {
  projectId: string;
  files: any[];
  onSelectFile?: (file: FileNode) => void;
}

const FileTreeNode: React.FC<{
  node: FileNode;
  level: number;
  projectId: string;
  onSelectFile?: (file: FileNode) => void;
}> = memo(({ node, level, projectId, onSelectFile }) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (node.type === 'folder') {
        setExpanded(!expanded);
      } else {
        onSelectFile?.(node);
      }
    },
    [node, expanded, onSelectFile]
  );

  const indent = level * 16;

  return (
    <div className="select-none">
      <div
        className="flex items-center py-1 px-2 hover:bg-gray-50 cursor-pointer group"
        style={{ paddingLeft: `${indent}px` }}
        onClick={handleClick}
      >
        <div className="flex items-center flex-1 min-w-0">
          {node.type === 'folder' ? (
            <>
              <span className="mr-1">
                {expanded ? (
                  <ChevronDown size={14} className="text-gray-500" />
                ) : (
                  <ChevronRight size={14} className="text-gray-500" />
                )}
              </span>
              <Folder
                size={16}
                className="text-yellow-500 mr-2 flex-shrink-0"
              />
            </>
          ) : (
            <span className="mr-2 ml-4 flex-shrink-0">
              <FileTypeIcon fileName={node.name} size={18} />
            </span>
          )}
          <span className="text-sm text-gray-700 truncate" title={node.name}>
            {node.name}
          </span>
        </div>
      </div>

      {node.type === 'folder' && expanded && node.children && (
        <div>
          {node.children.map(child => (
            <FileTreeNode
              key={child.id || child.path}
              node={child}
              level={level + 1}
              projectId={projectId}
              onSelectFile={onSelectFile}
            />
          ))}
        </div>
      )}
    </div>
  );
});

FileTreeNode.displayName = 'FileTreeNode';

// 处理文件数据，递归转换树形结构
const processFiles = (files: any[], parentPath = ''): FileNode[] => {
  if (!files || !Array.isArray(files)) return [];

  const result = files
    .map(file => {
      if (!file) return null;

      const name = file.name || 'Unknown';
      const currentPath = parentPath ? `${parentPath}/${name}` : name;

      const node: FileNode = {
        id: file.id || currentPath,
        name: name,
        path: file.path || currentPath,
        type: file.type === 'folder' ? 'folder' : 'file',
        size: file.size,
        modifiedAt: file.modifiedAt,
        extension: name.includes('.') ? name.split('.').pop() : undefined,
        children: file.children
          ? processFiles(file.children, currentPath)
          : undefined,
      };
      return node;
    })
    .filter((node): node is FileNode => node !== null);

  // 排序：文件夹在前，文件在后，同类型按字母排序
  const sortNodes = (nodes: FileNode[]): FileNode[] => {
    return nodes
      .sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'folder' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      })
      .map(node => ({
        ...node,
        children: node.children ? sortNodes(node.children) : undefined,
      }));
  };

  return sortNodes(result);
};

export default function ProjectFileList({
  projectId,
  files,
  onSelectFile,
}: ProjectFileListProps) {
  const fileTree = processFiles(files);

  if (fileTree.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
        No files in this project
      </div>
    );
  }

  return (
    <div className="py-2">
      {fileTree.map(node => (
        <FileTreeNode
          key={node.id || node.path}
          node={node}
          level={0}
          projectId={projectId}
          onSelectFile={onSelectFile}
        />
      ))}
    </div>
  );
}
